import { Frustum, Matrix4, Vector2, Vector3 } from "three";
import { Cache, DisparityTile, GeometryUtils, isDefined, isArray, MatrixUtils, QuadtreeUtils, } from "../../infra3d";
import { Obj, MEASUREMODE } from "../internal";
import { Elevator2, MonoApproxMeasurePoint, MonoplotMeasurePoint, MultiViewMeasurePoint, MultiViewMeasureSequence, PanoMeasurePoint, PanoTwoClickMeasureSequence, StereoMeasurePoint, StereoMeasureSequence, STREAMTYPE, } from "./internal";
import { CubemapGeoFrameCollection } from "../geoframe/cubemapgeoframecollection";
import { AuthMode } from "../viewer/AuthMode";
/**
 * Measure Handler
 * @class MeasureHandler
 * @author Benjamin Loesch
 * @extends {Obj}
 *
 */
export class MeasureHandler extends Obj {
    constructor(scene) {
        super();
        this.activeMeasureSequence = null;
        this.measureSequenceInProgress = false;
        this.scene = scene;
        this.engine = scene.engine;
        this.activeMeasureSequence = null;
        this.streamCache = new Cache(4, false);
    }
    /**
     * Creates a mmMeasurePoint-Object, with measure-sequence
     *
     * @method createMeasurePointInMeasureMode
     * @param {GeoFrame} geoframe
     * @param {number} mousex Mouse-Coordinate x
     * @param {number} mousey Mouse-Coordinate y
     * @param {?number=}maxdist Maximal distance for a successful measurement
     * @param successcbf
     * @param failedcbf
     * @param measuremode
     * @return {MeasurePoint}
     */
    createMeasurePointInMeasureMode(geoframe, mousex, mousey, maxdist, successcbf, failedcbf, measuremode) {
        if (this.activeMeasureSequence != null)
            return;
        this.cbs = successcbf;
        this.cbf = failedcbf;
        if (measuremode == MEASUREMODE.DEFINED_BY_SCENE)
            measuremode = this.scene.getMeasureMode();
        switch (measuremode) {
            case MEASUREMODE.MONOPLOTTING:
                this.createMonoplotMeasurePoint(geoframe, mousex, mousey, maxdist, successcbf, failedcbf, true);
                break;
            case MEASUREMODE.LEFTRIGHT:
                this.measureSequenceInProgress = true;
                this.activeMeasureSequence = new StereoMeasureSequence(this.scene, (pos) => {
                    this._sucesscbf(pos);
                }, (error) => {
                    this._failedCbf(error);
                }, mousex, mousey);
                break;
            case MEASUREMODE.MULTIVIEW:
                this.measureSequenceInProgress = true;
                this.activeMeasureSequence = new MultiViewMeasureSequence(this.scene, (pos) => {
                    this._sucesscbf(pos);
                }, (error) => {
                    this._failedCbf(error);
                }, mousex, mousey);
                break;
            case MEASUREMODE.MONOAPPROXIMATION:
                this.createMonoApproxMeasurePoint(geoframe, mousex, mousey, maxdist, successcbf, failedcbf, true);
                break;
            case MEASUREMODE.PANODEPTHMAPINTERSECTION:
                this.createPanoMeasurePoint(geoframe, mousex, mousey, maxdist, successcbf, failedcbf);
                break;
            case MEASUREMODE.PANODEPTHMAPINTERSECTION_MULTIVIEW_COMBINED:
                this.createPanoMeasurePoint(geoframe, mousex, mousey, maxdist, (mp) => {
                    this._sucesscbf(mp);
                }, () => {
                    if (this.measureSequenceInProgress)
                        return;
                    this.measureSequenceInProgress = true;
                    this.activeMeasureSequence = new MultiViewMeasureSequence(this.scene, (pos) => {
                        this._sucesscbf(pos);
                    }, (error) => {
                        this._failedCbf(error);
                    }, mousex, mousey);
                });
                break;
            case MEASUREMODE.PANO_TWO_CLICK:
                this.measureSequenceInProgress = true;
                this.activeMeasureSequence = new PanoTwoClickMeasureSequence(this.scene, (pos) => {
                    this._sucesscbf(pos);
                }, (error) => {
                    this._failedCbf(error);
                }, mousex, mousey);
                break;
            case MEASUREMODE.PANODEPTHMAPINTERSECTION_PANO_TWO_CLICK_COMBINED:
                this.createPanoMeasurePoint(geoframe, mousex, mousey, maxdist, (mp) => {
                    this._sucesscbf(mp);
                }, () => {
                    if (this.measureSequenceInProgress)
                        return;
                    this.measureSequenceInProgress = true;
                    this.activeMeasureSequence = new PanoTwoClickMeasureSequence(this.scene, (pos) => {
                        this._sucesscbf(pos);
                    }, (error) => {
                        this._failedCbf(error);
                    }, mousex, mousey);
                });
        }
    }
    abortMeasureSequence() {
        if (this.measureSequenceInProgress) {
            this.activeMeasureSequence.destroy();
            this.activeMeasureSequence = null;
            this.measureSequenceInProgress = false;
            if (this.cbf)
                this.cbf("Measure Sequence aborted.");
        }
    }
    _sucesscbf(pos) {
        this.cbs(pos);
        this.cbs = null;
        this.cbf = null;
        this.abortMeasureSequence();
    }
    _failedCbf(reason) {
        this.cbf(reason);
        this.cbs = null;
        this.cbf = null;
        this.abortMeasureSequence();
    }
    /**
     * Create a Monoplot MeasurePoint
     * @method createMonoplotMeasurePoint
     * @param geoframe
     * @param mousex
     * @param mousey
     * @param maxdist
     * @param cbs
     * @param cbf
     * @param async
     * @returns {MonoplotMeasurePoint}
     */
    createMonoplotMeasurePoint(geoframe, mousex, mousey, maxdist, cbs, cbf, async) {
        const minfo = geoframe.getMeasureInfo();
        if ([STREAMTYPE.MONO, STREAMTYPE.STEREO, STREAMTYPE.CUBEMAP].indexOf(minfo.streamtype) >= 0) {
            const mp = new MonoplotMeasurePoint(geoframe, mousex, mousey, maxdist);
            if (async) {
                if (mp.warning) {
                    cbf(mp.warntext);
                }
                else {
                    cbs(mp);
                }
            }
            else {
                return mp;
            }
        }
    }
    /**
     * Create a PanoMeasurePoint by DepthMap Intersection
     * @param geoframe
     * @param mousex
     * @param mousey
     * @param maxdist
     * @param cbs
     * @param cbf
     * @returns {MonoplotMeasurePoint}
     */
    createPanoMeasurePoint(geoframe, mousex, mousey, maxdist, cbs, cbf) {
        const pmpsuccess = (mp) => {
            cbs(mp);
        };
        const pmpfailed = (msg) => {
            cbf(msg);
        };
        new PanoMeasurePoint(geoframe, mousex, mousey, maxdist, pmpsuccess, pmpfailed);
    }
    /**
     * Create a MonoApproxMeasurePoint
     * @param geoframe
     * @param mousex
     * @param mousey
     * @param maxdist
     * @param cbs
     * @param cbf
     * @param async
     * @returns {MonoplotMeasurePoint}
     */
    createMonoApproxMeasurePoint(geoframe, mousex, mousey, maxdist, cbs, cbf, async) {
        const mp = new MonoApproxMeasurePoint(geoframe, mousex, mousey, maxdist);
        if (async) {
            cbs(mp);
        }
        else {
            return mp;
        }
    }
    /**
     * Create a StereoMeasurePoint - normally this function is called in StereoMeasureSequence
     * @param geoframe
     * @param mouseX1
     * @param mouseY1
     * @param mouseX2
     * @param mouseY2
     * @param maxdist
     * @returns {StereoMeasurePoint}
     */
    createStereoMeasurePoint(geoframe, mouseX1, mouseY1, mouseX2, mouseY2, maxdist) {
        const mp = new StereoMeasurePoint(geoframe, mouseX1, mouseY1, mouseX2, mouseY2, maxdist);
        return mp;
    }
    createMultiViewMeasurePoint(geoframe, updatecbf, resultcbf) {
        const mp = new MultiViewMeasurePoint(geoframe, updatecbf, resultcbf);
        return mp;
    }
    /**--------------------------
     *  Measure helpers...
     *--------------------------*/
    /**
     * transform mouse to quadtree - map [0...1 ; 0...1] coordinates
     * @method mouseToMap
     * @param mx
     * @param my
     * @param geoframe
     * @param {boolean}all if true the function returns all resulting coordinates (on intersection of pano-callote
     * there are 2 results.). The results are sorted by its distance to the callote. The closest callote-distance
     * ist the one you see.
     * @return {object}
     */
    mouseToMap(mx, my, geoframe, all) {
        if (geoframe == null || !isDefined(geoframe))
            geoframe = this.scene.getCurrentGeoframe();
        if (all == null || !isDefined(all))
            all = false;
        const minfo = geoframe.getMeasureInfo();
        let retval = null;
        if ([STREAMTYPE.MONO, STREAMTYPE.STEREO].indexOf(minfo.streamtype) >= 0) {
            const map = geoframe.parentGeoFrameCollection.imagequadtree.mouseToMap(mx, my);
            retval = [new MouseToMapInfo(new Vector2(map[0], map[1]), 0)];
        }
        else if (minfo.streamtype == STREAMTYPE.CUBEMAP) {
            const map = geoframe.parentGeoFrameCollection.imagequadtree.mouseToMap(mx, my);
            if (map)
                retval = [new MouseToMapInfo(new Vector2(map[0], map[1]), map[2], null, null, map[3], map[4])];
        }
        else if (minfo.streamtype == STREAMTYPE.CALOTTE) {
            const panoCoords = geoframe.parentGeoFrameCollection.imagequadtree.mouseToMap(mx, my, true);
            panoCoords.sort(function (a, b) {
                return a[4] - b[4];
            });
            //calc max-value on image
            const max_map = this.sensorToMap(new Vector2(minfo.imagemeta.width, minfo.imagemeta.height));
            const panoCoordsCollection = [];
            for (let i = 0; i < panoCoords.length; i++) {
                //check if the map-coords are inside the image
                if (panoCoords[i][0] <= max_map.x && panoCoords[i][1] <= max_map.y) {
                    const mmi = new MouseToMapInfo(new Vector2(panoCoords[i][0], panoCoords[i][1]), panoCoords[i][2], panoCoords[i][3], panoCoords[i][4]);
                    panoCoordsCollection.push(mmi);
                }
            }
            retval = all ? panoCoordsCollection : panoCoordsCollection.length > 0 ? [panoCoordsCollection[0]] : [];
        }
        return retval;
    }
    /**
     * transform quadtree - map [0...1 ; 0...1] to mouse coordinates
     *
     * @method mapToMouse
     * @param map_x
     * @param map_y
     * @param geoframe
     * @param sensorid
     * @return {Vector2}
     */
    mapToMouse(map_x, map_y, sensorid, geoframe) {
        if (geoframe == null || !isDefined(geoframe))
            geoframe = this.scene.getCurrentGeoframe();
        if (sensorid == null || !isDefined(sensorid))
            sensorid = 0;
        // console.log(sensorid);
        const minfo = geoframe.getMeasureInfo();
        let mouse_xy = null;
        if ([STREAMTYPE.MONO, STREAMTYPE.STEREO].indexOf(minfo.streamtype) >= 0) {
            mouse_xy = geoframe.parentGeoFrameCollection.imagequadtree.mapToMouse(map_x, map_y);
        }
        else if (minfo.streamtype == STREAMTYPE.CUBEMAP) {
            mouse_xy = geoframe.parentGeoFrameCollection.imagequadtree.mapToMouse(map_x, map_y, sensorid);
        }
        else if (minfo.streamtype == STREAMTYPE.CALOTTE) {
            mouse_xy = geoframe.parentGeoFrameCollection.imagequadtree.mapToMouse(map_x, map_y, sensorid);
        }
        return new Vector2(mouse_xy[0], mouse_xy[1]);
    }
    mapToSensor(map, geoframe) {
        if (geoframe == null || !isDefined(geoframe))
            geoframe = this.scene.getCurrentGeoframe();
        const minfo = geoframe.getMeasureInfo();
        const quadtreesize = minfo.imagemeta.tilesize * Math.pow(2, minfo.imagemeta.maxzoom);
        const sensor_xy = new Vector2();
        sensor_xy.x = map.x * quadtreesize;
        sensor_xy.y = map.y * quadtreesize;
        return sensor_xy;
    }
    sensorToMap(sensor, geoframe) {
        if (isArray(sensor)) {
            sensor = new Vector2().fromArray(sensor);
        }
        if (geoframe == null || !isDefined(geoframe))
            geoframe = this.scene.getCurrentGeoframe();
        const minfo = geoframe.getMeasureInfo();
        const quadtreesize = minfo.imagemeta.tilesize * Math.pow(2, minfo.imagemeta.maxzoom);
        const map_xy = new Vector2();
        map_xy.x = sensor.x / quadtreesize;
        map_xy.y = sensor.y / quadtreesize;
        return map_xy;
    }
    sensorToImage(sensor, geoframe) {
        if (geoframe == null || !isDefined(geoframe))
            geoframe = this.scene.getCurrentGeoframe();
        const minfo = geoframe.getMeasureInfo();
        const image_xy = new Vector2();
        image_xy.x = (sensor.x - minfo.imagemeta.width / 2.0) * minfo.imagemeta.pixsize * 0.001;
        image_xy.y = -1 * (sensor.y - minfo.imagemeta.height / 2.0) * minfo.imagemeta.pixsize * 0.001;
        return image_xy;
    }
    imageToSensor(image, geoframe) {
        if (geoframe == null || !isDefined(geoframe))
            geoframe = this.scene.getCurrentGeoframe();
        const minfo = geoframe.getMeasureInfo();
        const sensor_xy = new Vector2();
        sensor_xy.x = image.x / (minfo.imagemeta.pixsize * 0.001) + minfo.imagemeta.width / 2.0;
        sensor_xy.y = image.y / (-1.0 * minfo.imagemeta.pixsize * 0.001) + minfo.imagemeta.height / 2.0;
        return sensor_xy;
    }
    imageToModel(image, depth, sensorid, geoframe) {
        if (geoframe == null || !isDefined(geoframe))
            geoframe = this.scene.getCurrentGeoframe();
        if (sensorid == null || !isDefined(sensorid))
            sensorid = 0;
        const minfo = geoframe.getMeasureInfo();
        const model = new Vector3();
        if ([STREAMTYPE.MONO, STREAMTYPE.STEREO, STREAMTYPE.CUBEMAP].indexOf(minfo.streamtype) >= 0) {
            model.x = image.x;
            model.y = image.y;
            model.z = -1 * minfo.imagemeta.focallength;
            model.normalize();
            if (depth != null && isDefined(depth))
                model.multiplyScalar(depth / (-1 * model.z));
        }
        else if (minfo.streamtype == STREAMTYPE.CALOTTE) {
            const r = Math.sqrt(image.x * image.x + image.y * image.y);
            const theta = r / (-1 * minfo.sensors[sensorid].focallength);
            model.x = (image.x / r) * Math.sin(theta);
            model.y = (image.y / r) * Math.sin(theta);
            model.z = Math.cos(theta);
            if (depth != null && isDefined(depth))
                model.multiplyScalar(depth / (-1 * model.z));
        }
        return model;
    }
    modelToImage(model, sensorid, geoframe) {
        if (geoframe == null || !isDefined(geoframe))
            geoframe = this.scene.getCurrentGeoframe();
        if (sensorid == null || !isDefined(sensorid))
            sensorid = 0;
        const minfo = geoframe.getMeasureInfo();
        const image = new Vector2();
        if ([STREAMTYPE.MONO, STREAMTYPE.STEREO].indexOf(minfo.streamtype) >= 0 ||
            minfo.streamtype == STREAMTYPE.CUBEMAP) {
            image.x = minfo.imagemeta.focallength * -1.0 * (model.x / model.z);
            image.y = minfo.imagemeta.focallength * -1.0 * (model.y / model.z);
        }
        else if (minfo.streamtype == STREAMTYPE.CALOTTE) {
            const r = Math.sqrt(model.x * model.x + model.y * model.y);
            image.x = minfo.sensors[sensorid].focallength * -1 * (model.x / r) * Math.atan(r / model.z);
            image.y = minfo.sensors[sensorid].focallength * -1 * (model.y / r) * Math.atan(r / model.z);
        }
        return image;
    }
    modelToFrame(model, sensorid, geoframe, cubeorientation) {
        if (geoframe == null || !isDefined(geoframe))
            geoframe = this.scene.getCurrentGeoframe();
        if (sensorid == null || !isDefined(sensorid))
            sensorid = 0;
        if (cubeorientation == null || !isDefined(cubeorientation))
            cubeorientation = {
                domega: 0,
                dphi: 0,
                dkappa: 0,
            };
        const minfo = geoframe.getMeasureInfo();
        const frame = new Vector3();
        frame.copy(model);
        let R_c_c0;
        // lb sensor position
        const sensorpos = minfo.sensors[sensorid].position;
        if (minfo.streamtype == STREAMTYPE.CUBEMAP) {
            // use cube orientation
            R_c_c0 = MeasureHandler.getRotationMatrix((cubeorientation.domega * Math.PI) / 180, (cubeorientation.dphi * Math.PI) / 180, (cubeorientation.dkappa * Math.PI) / 180);
        }
        else {
            R_c_c0 = MeasureHandler.getRotationMatrix((sensorpos.domega * Math.PI) / 180, (sensorpos.dphi * Math.PI) / 180, (sensorpos.dkappa * Math.PI) / 180);
        }
        const r_c0_c = new Vector3(sensorpos.dx, sensorpos.dy, sensorpos.dz);
        frame.applyMatrix4(R_c_c0);
        frame.add(r_c0_c);
        return frame;
    }
    frameToWorld(frame, geoframe) {
        if (geoframe == null || !isDefined(geoframe))
            geoframe = this.scene.getCurrentGeoframe();
        const world = new Vector3();
        world.copy(frame);
        const R_c0_w = MeasureHandler.getRotationMatrix((geoframe.omega * Math.PI) / 180, (geoframe.phi * Math.PI) / 180, (geoframe.kappa * Math.PI) / 180);
        const r_w_c0 = new Vector3(geoframe.easting, geoframe.northing, geoframe.height);
        world.applyMatrix4(R_c0_w);
        world.add(r_w_c0);
        return world;
    }
    worldToFrame(world, geoframe) {
        if (isArray(world)) {
            world = new Vector3().fromArray(world);
        }
        if (geoframe == null || !isDefined(geoframe))
            geoframe = this.scene.getCurrentGeoframe();
        const frame = new Vector3();
        frame.copy(world);
        const R_c0_w = MeasureHandler.getRotationMatrix((geoframe.omega * Math.PI) / 180, (geoframe.phi * Math.PI) / 180, (geoframe.kappa * Math.PI) / 180);
        const r_w_c0 = new Vector3(geoframe.easting, geoframe.northing, geoframe.height);
        frame.sub(r_w_c0);
        frame.applyMatrix4(R_c0_w.transpose());
        return frame;
    }
    frameToModel(frame, sensorid, geoframe, cubeorientation) {
        if (isArray(frame)) {
            frame = new Vector3().fromArray(frame);
        }
        if (geoframe == null || !isDefined(geoframe))
            geoframe = this.scene.getCurrentGeoframe();
        if (sensorid == null || !isDefined(sensorid))
            sensorid = 0;
        const minfo = geoframe.getMeasureInfo();
        const model = new Vector3();
        model.copy(frame);
        const sensorpos = minfo.sensors[sensorid].position;
        let R_c_c0;
        if (minfo.streamtype == STREAMTYPE.CUBEMAP) {
            R_c_c0 = MeasureHandler.getRotationMatrix((cubeorientation.domega * Math.PI) / 180, (cubeorientation.dphi * Math.PI) / 180, (cubeorientation.dkappa * Math.PI) / 180);
        }
        else {
            R_c_c0 = MeasureHandler.getRotationMatrix((sensorpos.domega * Math.PI) / 180, (sensorpos.dphi * Math.PI) / 180, (sensorpos.dkappa * Math.PI) / 180);
        }
        const r_c0_c = new Vector3(sensorpos.dx, sensorpos.dy, sensorpos.dz);
        model.sub(r_c0_c);
        model.applyMatrix4(R_c_c0.transpose());
        return model;
    }
    modelToWorld(model, sensorid, geoframe, cubeorientation) {
        if (geoframe == null || !isDefined(geoframe))
            geoframe = this.scene.getCurrentGeoframe();
        if (sensorid == null || !isDefined(sensorid))
            sensorid = 0;
        if (cubeorientation == null || !isDefined(cubeorientation))
            cubeorientation = {
                domega: 0,
                dphi: 0,
                dkappa: 0,
            };
        return this.frameToWorld(this.modelToFrame(model, sensorid, geoframe, cubeorientation), geoframe);
    }
    worldToModel(world, sensorid, geoframe, cubeorientation) {
        if (isArray(world)) {
            world = new Vector3().fromArray(world);
        }
        if (geoframe == null || !isDefined(geoframe))
            geoframe = this.scene.getCurrentGeoframe();
        if (sensorid == null || !isDefined(sensorid))
            sensorid = 0;
        return this.frameToModel(this.worldToFrame(world, geoframe), sensorid, geoframe, cubeorientation);
    }
    worldToMapinfo(world, geoframe, mindist, maxdist, cubeorientations) {
        if (geoframe == null || !isDefined(geoframe))
            geoframe = this.scene.getCurrentGeoframe();
        if (mindist == null || !isDefined(mindist))
            mindist = 2.0;
        if (maxdist == null || !isDefined(maxdist))
            maxdist = 30.0;
        const mtominfoarray = [];
        const mi = geoframe.getMeasureInfo();
        if (mi.streamtype == STREAMTYPE.CUBEMAP) {
            const cubemapgeoframecollection = geoframe.parentGeoFrameCollection;
            if (cubeorientations == null || !isDefined(cubeorientations)) {
                if (isDefined(cubemapgeoframecollection) && cubemapgeoframecollection != null) {
                    cubeorientations = cubemapgeoframecollection.getCubeOrientations();
                }
                else {
                    cubeorientations = [];
                    for (const cubeface of CubemapGeoFrameCollection.cubefaces) {
                        cubeorientations.push(cubeface.position);
                    }
                }
            }
            for (let i = 0; i < mi.sensors.length; i++) {
                for (let j = 0; j < cubeorientations.length; j++) {
                    const r_c_p = this.worldToModel(world, i, geoframe, cubeorientations[j]);
                    if (r_c_p.z < 0 && r_c_p.length() > mindist && r_c_p.length() < maxdist) {
                        const uv = this.imageToSensor(this.modelToImage(r_c_p, i, geoframe), geoframe);
                        if (uv.x > 0 && uv.x < mi.imagemeta.width && uv.y > 0 && uv.y < mi.imagemeta.height) {
                            const map = this.sensorToMap(uv, geoframe);
                            let sensorid = 0;
                            if (cubemapgeoframecollection)
                                sensorid = cubemapgeoframecollection.mapToSensorId(map.x, map.y, j);
                            if (sensorid == i) {
                                const mtomap = new MouseToMapInfo(map, i, mi.imagemeta.maxzoom, r_c_p.length(), j, cubeorientations[j]);
                                mtominfoarray.push(mtomap);
                            }
                        }
                    }
                }
            }
        }
        else {
            for (let i = 0; i < mi.sensors.length; i++) {
                const r_c_p = this.worldToModel(world, i, geoframe);
                if (r_c_p.z < 0 && r_c_p.length() > mindist && r_c_p.length() < maxdist) {
                    const uv = this.imageToSensor(this.modelToImage(r_c_p, i, geoframe), geoframe);
                    if (uv.x > 0 && uv.x < mi.imagemeta.width && uv.y > 0 && uv.y < mi.imagemeta.height) {
                        const mtomap = new MouseToMapInfo(this.sensorToMap(uv, geoframe), i, mi.imagemeta.maxzoom, r_c_p.length());
                        mtominfoarray.push(mtomap);
                    }
                }
            }
        }
        return mtominfoarray;
    }
    mapinfoToWorld(mapinfo, geoframe, cbf, cbf_failed) {
        if (geoframe == null || !isDefined(geoframe))
            geoframe = this.scene.getCurrentGeoframe();
        this.mapToDisparity(mapinfo.coords, mapinfo.sensorid, geoframe, (disp_back) => {
            if (disp_back[0] > 0) {
                const p_back = this.modelToWorld(this.imageToModel(this.sensorToImage(this.mapToSensor(mapinfo.coords, geoframe), geoframe), disp_back[1], mapinfo.sensorid, geoframe), mapinfo.sensorid, geoframe);
                if (cbf !== null && isDefined(cbf))
                    cbf(p_back, disp_back[2]);
            }
            else {
                if (cbf_failed !== null && isDefined(cbf_failed))
                    cbf_failed(new Vector3(), 0);
            }
        }, (disp_back) => {
            if (cbf_failed !== null && isDefined(cbf_failed))
                cbf_failed(new Vector3(), 0);
        });
    }
    mapToDisparity(map, sensorid, geoframe, cbf, cbf_failed) {
        if (geoframe == null || !isDefined(geoframe))
            geoframe = this.scene.getCurrentGeoframe();
        if (sensorid == null || !isDefined(sensorid))
            sensorid = 0;
        const mi = geoframe.getMeasureInfo();
        let disparityinfo = [0, 0, 0];
        //todo: better metadata and check for if the image has depth map or not
        let hasdepthmap = false;
        if (sensorid == 0 &&
            (mi.depthmapmeta.structure == "l" ||
                mi.depthmapmeta.structure == "lr" ||
                mi.depthmapmeta.structure == "all")) {
            hasdepthmap = true;
        }
        else if (sensorid == 1 && (mi.depthmapmeta.structure == "lr" || mi.depthmapmeta.structure == "all")) {
            hasdepthmap = true;
        }
        else if (sensorid > 1 && mi.depthmapmeta.structure == "all") {
            hasdepthmap = true;
        }
        if (this.scene.authmode == AuthMode.view)
            hasdepthmap = false;
        if (hasdepthmap) {
            if (geoframe.parentGeoFrameCollection.hasloaded) {
                if ([STREAMTYPE.STEREO, STREAMTYPE.MONO].indexOf(mi.streamtype) >= 0) {
                    disparityinfo = (geoframe.parentGeoFrameCollection).disparityquadtree.readDisparity(map.x, map.y, true);
                }
                else if (mi.streamtype == STREAMTYPE.CUBEMAP) {
                    disparityinfo = (geoframe.parentGeoFrameCollection.imagequadtree).readDisparity(map.x, map.y, sensorid);
                }
                else {
                    if (cbf_failed !== null && isDefined(cbf_failed))
                        cbf_failed([0, 0, 0]);
                }
            }
            else if (mi.depthmapmeta !== null) {
                const quadtreesize = Math.pow(2, mi.depthmapmeta.maxzoom) * mi.imagemeta.tilesize;
                const tile = new DisparityTile(QuadtreeUtils.NormalizedCoordToQuadKey(map.x, map.y, mi.depthmapmeta.maxzoom), geoframe.dataPath, mi.depthmapmeta.width / quadtreesize, mi.depthmapmeta.height / quadtreesize, mi.depthmapmeta.tilesize, geoframe.parentGeoFrameCollection.infrascene.traversalState, (tile) => {
                    disparityinfo = tile.getDisparityValue(map.x, map.y, false);
                    const disparity = (disparityinfo[0] * mi.depthmapmeta.numdisparities) / 65535;
                    const disparityInmm = disparity * mi.depthmapmeta.pixsize * 0.001;
                    const quality = disparityinfo[1];
                    //calculate the depth in m
                    const depth = (mi.depthmapmeta.focallength * mi.depthmapmeta.basis) / disparityInmm;
                    if (cbf !== null && isDefined(cbf))
                        cbf([disparity, depth, quality]);
                }, (tile) => {
                    if (cbf_failed !== null && isDefined(cbf_failed))
                        cbf_failed([0, 0, 0]);
                });
            }
            else {
                if (cbf_failed !== null && isDefined(cbf_failed))
                    cbf_failed([0, 0, 0]);
            }
        }
        else {
            if (cbf_failed !== null && isDefined(cbf_failed))
                cbf_failed([0, 0, 0]);
        }
        const disparity = (disparityinfo[0] * mi.depthmapmeta.numdisparities) / 65535;
        // const disparityInmm = disparity * mi.depthmapmeta.pixsize * 0.001;
        //
        const quality = disparityinfo[3];
        //
        // //calculate the depth in m
        // const depth = (mi.normck * mi.normbasis) / disparityInmm;
        const depth = this._disparityToDepth(disparity, geoframe);
        return [disparity, depth, quality];
    }
    /**
     *
     * @param {number} disparity in pixel
     * @param {GeoFrame} geoframe
     * @returns {number} depth in m
     * @private
     */
    _disparityToDepth(disparity, geoframe) {
        if (geoframe == null || !isDefined(geoframe))
            geoframe = this.scene.getCurrentGeoframe();
        const mi = geoframe.getMeasureInfo();
        const focallength = mi.depthmapmeta.focallength;
        const basis = mi.depthmapmeta.basis;
        const pixsize_mm = mi.depthmapmeta.pixsize * 0.001;
        // if (mi.streamtype == STREAMTYPE.CUBEMAP) {
        //     focallength = mi.depthmapmeta.width / 2;
        //     basis = 1.0;
        //     // pixsize_mm = 2.0;
        // }
        return (focallength * basis) / (disparity * pixsize_mm);
    }
    mapToLatLong(map, sensorid, geoframe, centerpoint, cubefaceindex) {
        if (geoframe == null || !isDefined(geoframe))
            geoframe = this.scene.getCurrentGeoframe();
        const mi = geoframe.getMeasureInfo();
        let coords;
        if (mi.streamtype == STREAMTYPE.CALOTTE) {
            coords = geoframe.parentGeoFrameCollection.imagequadtree.mapTo3D(map.x, map.y, sensorid);
        }
        else if (mi.streamtype == STREAMTYPE.CUBEMAP) {
            coords = geoframe.parentGeoFrameCollection.imagequadtree.mapTo3D(map.x, map.y, cubefaceindex);
        }
        else {
            console.log("not implemented for stereogeoframecollection");
            return null;
        }
        //calc lat lon relative to a set centerpoint relative to pano-pos 0
        if (isDefined(centerpoint) && centerpoint != null)
            coords.sub(centerpoint);
        coords.normalize();
        const lat = (Math.atan2(coords.y, Math.sqrt(Math.pow(coords.x, 2) + Math.pow(coords.z, 2))) * 180) / Math.PI;
        const long = (Math.atan2(coords.z, coords.x) * 180) / Math.PI + 90;
        return new Vector2(lat, long);
    }
    /**
     * Calculates the mouse coordinates, given the world coordinates (easting,northing,height)
     * @param worldcoords
     * @param geoframe
     * @returns {Vector3}
     */
    worldToMouse(worldcoords, geoframe) {
        if (geoframe == null || !isDefined(geoframe))
            geoframe = this.scene.getCurrentGeoframe();
        const matModelViewProjection = geoframe.parentGeoFrameCollection.infrascene.traversalState.matMVP;
        const pickpos_intern = geoframe.parentGeoFrameCollection.infrascene.engine.coordToIntern(worldcoords.x, worldcoords.y, worldcoords.z);
        const pickpos3 = new Vector3(pickpos_intern[0], pickpos_intern[1], pickpos_intern[2]);
        const pickpos_screen = MatrixUtils.Mat4MulVec3(matModelViewProjection, pickpos3);
        let isInImage = true;
        if (pickpos_screen.x < -1 ||
            pickpos_screen.x > 1 ||
            isNaN(pickpos_screen.x) ||
            pickpos_screen.y < -1 ||
            pickpos_screen.y > 1 ||
            isNaN(pickpos_screen.y) ||
            pickpos_screen.z < -1 ||
            pickpos_screen.z > 1 ||
            isNaN(pickpos_screen.z)) {
            isInImage = false;
        }
        const w = geoframe.parentGeoFrameCollection.infrascene.engine.getContextWidth();
        const h = geoframe.parentGeoFrameCollection.infrascene.engine.getContextHeight();
        const x = w * 0.5 * (pickpos_screen.x + 1);
        const y = h - h * 0.5 * (pickpos_screen.y + 1);
        return new Vector3(x, y, +isInImage);
    }
    /**
     * Converts a Line in World Coordinates (defined by two points) into a line in Mouse Coordinates with Clipping (defined by two points in mouse coordinates)
     * @method worldLineToMouseLine
     * @param {Vector3} worldcoordsp1
     * @param {Vector3} worldcoordsp2
     * @return ?{Array.<number>} [mouseposp1_x,mouseposp1_y,mouseposp2_x,mouseposp2_y] returns null if there is no intersection between line and view frustum
     */
    worldLineToMouseLine(worldcoordsp1, worldcoordsp2) {
        const infrascene = this.scene.infrascene;
        const pickpos_intern_p1 = infrascene.engine.coordToInternT(worldcoordsp1.x, worldcoordsp1.y, worldcoordsp1.z);
        const pickpos_intern_p2 = infrascene.engine.coordToInternT(worldcoordsp2.x, worldcoordsp2.y, worldcoordsp2.z);
        const viewfrustum = new Frustum();
        viewfrustum.setFromProjectionMatrix(infrascene.traversalState.matMVP);
        const p1 = new Vector3().copy(pickpos_intern_p1);
        const p2 = new Vector3().copy(pickpos_intern_p2);
        const clippedpos = GeometryUtils.Clip3dLine(viewfrustum, p1, p2);
        if (clippedpos == null)
            return null; //no intersection with viewfrustum
        const clippedworldpos1 = infrascene.engine.internToCoordT(clippedpos[0].x, clippedpos[0].y, clippedpos[0].z);
        const clippedworldpos2 = infrascene.engine.internToCoordT(clippedpos[1].x, clippedpos[1].y, clippedpos[1].z);
        const clippedmousepos1 = this.worldToMouse(new Vector3(clippedworldpos1.x, clippedworldpos1.y, clippedworldpos1.z));
        const clippedmousepos2 = this.worldToMouse(new Vector3(clippedworldpos2.x, clippedworldpos2.y, clippedworldpos2.z));
        return [clippedmousepos1.x, clippedmousepos1.y, clippedmousepos2.x, clippedmousepos2.y];
    }
    getWorldOriForSensorId(sensorid, geoframe, faceorientation) {
        const sensori = [];
        const minfo = geoframe.getMeasureInfo();
        const model = new Vector3(0, 0, 0);
        const world = new Vector3();
        world.copy(model);
        const sensorpos = minfo.sensors[sensorid].position;
        let R_c_c0;
        if (minfo.streamtype == STREAMTYPE.CUBEMAP) {
            // add cube orientation
            R_c_c0 = MeasureHandler.getRotationMatrix((faceorientation.domega * Math.PI) / 180, (faceorientation.dphi * Math.PI) / 180, (faceorientation.dkappa * Math.PI) / 180);
        }
        else {
            R_c_c0 = MeasureHandler.getRotationMatrix((sensorpos.domega * Math.PI) / 180, (sensorpos.dphi * Math.PI) / 180, (sensorpos.dkappa * Math.PI) / 180);
        }
        const r_c0_c = new Vector3(sensorpos.dx, sensorpos.dy, sensorpos.dz);
        world.applyMatrix4(R_c_c0);
        world.add(r_c0_c);
        const R_c0_w = MeasureHandler.getRotationMatrix((geoframe.omega * Math.PI) / 180, (geoframe.phi * Math.PI) / 180, (geoframe.kappa * Math.PI) / 180);
        const r_w_c0 = new Vector3(geoframe.easting, geoframe.northing, geoframe.height);
        world.applyMatrix4(R_c0_w);
        const R_c_w = R_c0_w;
        R_c_w.multiply(R_c_c0);
        world.add(r_w_c0);
        const rot = MeasureHandler.getOPKFromRotationMatrix(R_c_w);
        sensori.push(world.x, world.y, world.z, rot[0], rot[1], rot[2]);
        return sensori;
    }
    /**
     * gets the 3d coord from a lv coordinate 2d using the depthmap
     * @param world
     * @param id
     * @param cbf
     * @param cbffailed
     * @param scope
     * @param sensorid
     * @param geoframe
     */
    getElevationFromPosition3(world, id, cbf, cbffailed, scope, sensorid, geoframe) {
        if (geoframe == null || !isDefined(geoframe))
            geoframe = this.scene.getCurrentGeoframe();
        const mi = geoframe.getMeasureInfo();
        if (mi.streamtype == STREAMTYPE.CUBEMAP) {
            const samplsize = 2;
            const ri_v = new Vector3(0, 0, geoframe.height - mi.height - 0.5 - world.z);
            const n = Math.ceil(ri_v.length() / samplsize);
            const plane_hits = {};
            let sensorid = 0;
            for (let i = 0; i < n; i++) {
                const world_i = new Vector3().copy(world);
                const ri_i = new Vector3().copy(ri_v);
                ri_i.multiplyScalar(i / n);
                world_i.add(ri_i);
                const mapinfos = this.worldToMapinfo(world_i, geoframe);
                for (let j = 0; j < mapinfos.length; j++) {
                    plane_hits[mapinfos[j].cubefaceindex] = i;
                    sensorid = mapinfos[j].sensorid;
                }
            }
            const cubefaces = [];
            for (const key in plane_hits)
                cubefaces.push({ cubefaceindex: key, order: plane_hits[key] });
            cubefaces.sort(function (a, b) {
                return b.order - a.order;
            });
            let k = 0;
            const runElevator = () => {
                this._runElevator(geoframe, sensorid, world.x, world.y, id, (X, Y, Z, id, quality) => {
                    k = cubefaces.length;
                    cbf.call(scope, X, Y, Z, id, quality);
                }, (id) => {
                    k += 1;
                    if (k < cubefaces.length) {
                        runElevator();
                    }
                    else {
                        if (isDefined(cbffailed))
                            cbffailed.call(scope, id);
                    }
                }, parseInt(cubefaces[k].cubefaceindex));
            };
            if (cubefaces.length == 0) {
                cbffailed.call(scope, id);
            }
            else {
                runElevator();
            }
        }
        else {
            this._runElevator(geoframe, sensorid, world.x, world.y, id, (X, Y, Z, id, quality) => {
                cbf.call(scope, X, Y, Z, id, quality);
            }, (id) => {
                if (isDefined(cbffailed)) {
                    cbffailed.call(scope, id);
                }
            });
        }
    }
    _runElevator(geoframe, sensorid, X, Y, id, cbf, cbffailed, cubefaceindex) {
        const elevator2 = new Elevator2(geoframe, sensorid, (Z, quality) => {
            if (Z !== null) {
                cbf(X, Y, Z, id, quality);
            }
            else {
                cbffailed(id);
            }
        }, cubefaceindex);
        elevator2.calc(X, Y, id);
    }
    static getRotationMatrix(Omega, Phi, Kappa) {
        const matRot = new Matrix4();
        matRot.set(Math.cos(Phi) * Math.cos(Kappa), -Math.cos(Phi) * Math.sin(Kappa), Math.sin(Phi), 0, Math.cos(Omega) * Math.sin(Kappa) + Math.sin(Omega) * Math.sin(Phi) * Math.cos(Kappa), Math.cos(Omega) * Math.cos(Kappa) - Math.sin(Omega) * Math.sin(Phi) * Math.sin(Kappa), -Math.sin(Omega) * Math.cos(Phi), 0, Math.sin(Omega) * Math.sin(Kappa) - Math.cos(Omega) * Math.sin(Phi) * Math.cos(Kappa), Math.sin(Omega) * Math.cos(Kappa) + Math.cos(Omega) * Math.sin(Phi) * Math.sin(Kappa), Math.cos(Omega) * Math.cos(Phi), 0, 0, 0, 0, 1);
        return matRot;
    }
    static getOPKFromRotationMatrix(R) {
        const Ra = R.toArray();
        let omega, kappa;
        const r13 = Ra[8]; //[0][2];
        const r23 = Ra[9]; //[1][2];
        const r33 = Ra[10]; //[2][2];
        const r11 = Ra[0]; //[0][0];
        const r12 = Ra[4]; //[0][1];
        const phi = Math.atan2(r13, Math.sqrt(Math.pow(r23, 2) + Math.pow(r33, 2)));
        const cosPhi = Math.cos(phi);
        if (cosPhi != 0.0) {
            omega = Math.atan2(-r23 / cosPhi, r33 / cosPhi);
            kappa = Math.atan2(-r12 / cosPhi, r11 / cosPhi);
        }
        else {
            //Singularitaet
            const r22 = R[1][1];
            const r32 = R[2][1];
            if (phi == Math.PI / 2) {
                omega = 0.0;
                kappa = Math.atan2(r22, r32);
            }
            else {
                omega = 0.0;
                kappa = -Math.atan2(r22, r32);
            }
        }
        return [(omega * 180) / Math.PI, (phi * 180) / Math.PI, (kappa * 180) / Math.PI];
    }
}
export class MouseToMapInfo {
    constructor(coords, sensorid, lod, distance, cubefaceindex, cubefaceorientation) {
        this.coords = coords;
        this.sensorid = sensorid;
        this.lod = lod;
        this.distance = distance;
        this.cubefaceorientation = cubefaceorientation;
        this.cubefaceindex = cubefaceindex;
    }
}
//# sourceMappingURL=measurehandler.js.map